import { PaletteMode, Components, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';
import dimensions from '../dimensions';

// Extend MUI Checkbox to support 'large' size
declare module '@mui/material/Checkbox' {
  interface CheckboxPropsSizeOverrides {
    large: true;
  }
}

export const MuiCheckboxTheme = (mode: PaletteMode): Components<Theme>['MuiCheckbox'] => {
  const { componentTokens, tokens } = themePalette(mode);
  const { spacing } = dimensions;

  return {
    defaultProps: {
      disableRipple: true,
      disableFocusRipple: true,
      disableTouchRipple: true,
      size: 'medium',
    },
    styleOverrides: {
      root: {
        padding: 0,
        paddingRight: spacing(3),
        transition: 'stroke 0.15s linear, fill 0.15s linear',

        // Unchecked state
        '& .MuiSvgIcon-root rect': {
          stroke: componentTokens.selectors.colors.checkbox.notSelected.stroke.resting,
          fill: componentTokens.selectors.colors.checkbox.notSelected.background.resting,
          rx: spacing(0.5),
        },

        // Hover on unchecked
        '&:hover .MuiSvgIcon-root rect': {
          stroke: componentTokens.selectors.colors.checkbox.notSelected.stroke.hover,
          fill: componentTokens.selectors.colors.checkbox.notSelected.background.hover,
        },

        // Focus on unchecked
        '&.Mui-focusVisible .MuiSvgIcon-root rect': {
          stroke: componentTokens.selectors.colors.checkbox.notSelected.stroke.hover,
          fill: componentTokens.selectors.colors.checkbox.notSelected.background.hover,
        },

        // Checked state
        '&.Mui-checked .MuiSvgIcon-root rect': {
          stroke: componentTokens.selectors.colors.checkbox.selected.stroke.resting,
          fill: componentTokens.selectors.colors.checkbox.selected.background.resting,
        },

        // Hover on checked
        '&.Mui-checked:hover .MuiSvgIcon-root rect': {
          stroke: componentTokens.selectors.colors.checkbox.selected.stroke.hover,
          fill: componentTokens.selectors.colors.checkbox.selected.background.hover,
        },

        // Focus on checked
        '&.Mui-checked.Mui-focusVisible .MuiSvgIcon-root rect': {
          stroke: componentTokens.selectors.colors.checkbox.selected.stroke.hover,
          fill: componentTokens.selectors.colors.checkbox.selected.background.hover,
        },

        // Indeterminate state
        '&.MuiCheckbox-indeterminate .MuiSvgIcon-root rect': {
          stroke: componentTokens.selectors.colors.checkbox.selected.stroke.resting,
          fill: componentTokens.selectors.colors.checkbox.selected.background.resting,
        },

        // Hover on indeterminate
        '&.MuiCheckbox-indeterminate:hover .MuiSvgIcon-root rect': {
          stroke: componentTokens.selectors.colors.checkbox.selected.stroke.hover,
          fill: componentTokens.selectors.colors.checkbox.selected.background.hover,
        },

        // Focus on indeterminate
        '&.MuiCheckbox-indeterminate.Mui-focusVisible .MuiSvgIcon-root rect': {
          stroke: componentTokens.selectors.colors.checkbox.selected.stroke.hover,
          fill: componentTokens.selectors.colors.checkbox.selected.background.hover,
        },

        '&.Mui-disabled': {
          opacity: 0.5,
        },

        // Invalid/error state styling - target rect element
        '&.MuiCheckbox-colorError .MuiSvgIcon-root rect': {
          stroke: tokens.error,
        },
        '&.MuiCheckbox-colorError:hover .MuiSvgIcon-root rect': {
          stroke: tokens.error,
        },
        '&.MuiCheckbox-colorError.Mui-focusVisible .MuiSvgIcon-root rect': {
          stroke: tokens.error,
        },
        '&.MuiCheckbox-colorError.Mui-checked .MuiSvgIcon-root rect': {
          stroke: tokens.error,
          fill: tokens.error,
        },
        '&.MuiCheckbox-colorError.Mui-checked:hover .MuiSvgIcon-root rect': {
          stroke: tokens.error,
          fill: tokens.error,
        },
        '&.MuiCheckbox-colorError.Mui-checked.Mui-focusVisible .MuiSvgIcon-root rect': {
          stroke: tokens.error,
          fill: tokens.error,
        },
        '&.MuiCheckbox-colorError.MuiCheckbox-indeterminate .MuiSvgIcon-root rect': {
          stroke: tokens.error,
          fill: tokens.error,
        },
        '&.MuiCheckbox-colorError.MuiCheckbox-indeterminate:hover .MuiSvgIcon-root rect': {
          stroke: tokens.error,
          fill: tokens.error,
        },
        '&.MuiCheckbox-colorError.MuiCheckbox-indeterminate.Mui-focusVisible .MuiSvgIcon-root rect':
          {
            stroke: tokens.error,
            fill: tokens.error,
          },
      },
      // Medium size in MUI = our "large" size (26px icon)
      sizeMedium: {
        '& .MuiSvgIcon-root': {
          width: '26px',
          height: '26px',
          color: 'white',
        },
      },
      // Small size = our "small" size (20px icon)
      sizeSmall: {
        '& .MuiSvgIcon-root': {
          width: '16px',
          height: '16px',
          color: 'white',
        },
      },
    },
  };
};
